import * as React from "react";
import classnames from "classnames";
import { Skeleton, Alert } from "@grandvisionhq/iris-ui";
import { useIntl } from "@grandvisionhq/state";
import { FormContext } from "../../Form/Form/Form.js";
import FormControlFieldWrapper from "../../FormControl/FormControl/FormControlWrapper.js";
import { maxLength } from "../../Util/Util/validations.js";
import PhoneNumberInput from "./PhoneNumberInput.js";
import { usePhoneNumberInputPackage } from "../../Util/react-phone-number-input/Util/react-phone-number-input/use-phone-number-input-package.js";
const initialStateAdapter = (initialState, id, validate) => ({
  value: initialState.value ?? "",
  isTouched: !!initialState?.isTouched,
  isValid: validate(initialState.value ?? ""),
  readonly: !!initialState?.readonly,
  name: id
});
const PhoneNumberInputContainer = ({
  defaultCountry,
  overrideTranslations,
  allowlist,
  countriesOrder,
  placeholder,
  id,
  onBlur,
  initialState,
  onChange,
  required,
  maxLength: maxLength$1,
  errorMessage,
  className,
  label,
  tooltip,
  optionalText,
  disabled,
  additionalValidations,
  localeForTranslations = "en",
  hideAsteriskSign = false,
  PhoneNumberInputModule,
  ...rest
}) => {
  const { getCountries, isValidPhoneNumber, parsePhoneNumber } = PhoneNumberInputModule;
  const executedOnMount = React.useRef(false);
  React.useEffect(() => {
    if (allowlist) {
      if (!allowlist.includes(defaultCountry)) {
        throw new Error("defaultCountry must be included in allowlisted countries");
      }
    }
  }, [allowlist, defaultCountry]);
  const translations = React.useMemo(() => {
    if (!localeForTranslations) {
      return overrideTranslations;
    }
    const countryNamesForLocale = new Intl.DisplayNames([localeForTranslations], {
      type: "region"
    });
    const baseTranslations = getCountries().reduce(
      (acc, countryCode) => ({ ...acc, [countryCode]: countryNamesForLocale.of(countryCode) }),
      {}
    );
    return { ...baseTranslations, ...overrideTranslations };
  }, [getCountries, localeForTranslations, overrideTranslations]);
  const validate = React.useCallback(
    (value) => {
      if (!required && value === "") {
        return true;
      }
      if (maxLength$1 && !maxLength(maxLength$1)(value)) {
        return false;
      }
      const isValidNumber = isValidPhoneNumber(value);
      return additionalValidations ? additionalValidations.reduce(
        (isValid2, validator) => validator(value) && isValid2,
        isValidNumber
      ) : isValidNumber;
    },
    [required, maxLength$1, isValidPhoneNumber, additionalValidations]
  );
  const [phoneState, setPhoneState] = React.useState(
    () => initialStateAdapter(initialState, id, validate)
  );
  const handleOnChange = React.useCallback(
    (formState) => {
      setPhoneState(formState);
      onChange?.(formState);
    },
    [onChange]
  );
  React.useEffect(() => {
    if (executedOnMount.current === false) {
      executedOnMount.current = true;
      handleOnChange({
        value: phoneState.value,
        name: id,
        errorMessage,
        isValid: validate(phoneState.value),
        readonly: phoneState.readonly
      });
    }
  }, [handleOnChange, id, phoneState.readonly, phoneState.value, validate]);
  const updateState = React.useCallback(() => {
    setPhoneState((prev) => {
      const state = {
        ...prev,
        ...initialStateAdapter(initialState, id, validate),
        // if element isTouched already, it shouldn't change back to false
        isTouched: prev.isTouched ? prev.isTouched : !!initialState.isTouched
      };
      if (JSON.stringify(prev) === JSON.stringify(state)) {
        return prev;
      } else {
        let savedPhoneNumber = state.value;
        let containsCountryCode = savedPhoneNumber.includes("+");
        let parsedPhoneNumber = parsePhoneNumber(savedPhoneNumber);
        if (containsCountryCode && allowlist) {
          const countryOfSavedPhoneNumber = parsedPhoneNumber?.country;
          if (!countryOfSavedPhoneNumber || !allowlist.includes(countryOfSavedPhoneNumber)) {
            savedPhoneNumber = "";
            containsCountryCode = false;
          }
        }
        if (!containsCountryCode) {
          parsedPhoneNumber = parsePhoneNumber(savedPhoneNumber, defaultCountry);
        }
        const isValidPhoneNumber2 = parsedPhoneNumber && validate(parsedPhoneNumber.number);
        const newValue = parsedPhoneNumber && isValidPhoneNumber2 ? parsedPhoneNumber.number : "";
        const newState = {
          ...state,
          value: newValue,
          // validate again in case this is an empty string
          isValid: validate(newValue)
        };
        if (!(initialState.value === "" && newValue === "")) {
          onChange?.(newState);
        }
        return newState;
      }
    });
  }, [allowlist, defaultCountry, id, initialState, onChange, parsePhoneNumber, validate]);
  React.useEffect(() => {
    updateState();
  }, [updateState]);
  const handleOnBlur = React.useCallback(() => {
    onBlur?.(phoneState);
    handleOnChange({
      ...phoneState,
      name: id,
      isTouched: true,
      isValid: validate(phoneState.value)
    });
  }, [handleOnChange, onBlur, phoneState, id, validate]);
  const { isTouched, readonly, isValid } = phoneState;
  const displayValid = isValid === true && isTouched === true && phoneState.value !== "" && !readonly;
  const displayError = isValid === false && isTouched === true && !readonly;
  const errorMessageId = `${id}-error-message`;
  const ariaDescribedBy = `${rest["aria-describedby"] || ""} ${errorMessageId}`.trim();
  const inputClasses = classnames("form-control__input", className, {
    "_phone-input": true,
    _is_valid: displayValid,
    _is_invalid: displayError
  });
  const onChangeValueOnly = React.useCallback(
    (value) => {
      handleOnChange({
        ...phoneState,
        value: value ?? "",
        isValid: validate(value ?? "")
      });
    },
    [handleOnChange, phoneState, validate]
  );
  return /* @__PURE__ */ React.createElement(
    PhoneNumberInput,
    {
      ...{
        defaultCountry,
        labels: translations,
        countries: allowlist,
        countryOptionsOrder: countriesOrder,
        /* do not display the +${countrycode} when a value
            that is set contains it since its already shown in svg*/
        initialValueFormat: "national",
        flagUrl: "https://assets.grandvision.io/global/images/flags/{XX}+Code.svg",
        international: false,
        addInternationalOption: false,
        placeholder,
        id,
        required,
        formControlWrapperProps: {
          className,
          label,
          hideAsteriskSign,
          tooltip,
          optionalText,
          errorMessageId,
          errorMessage,
          displayError
        },
        maxLength: maxLength$1,
        tooltip,
        disabled: disabled || phoneState.readonly,
        value: phoneState.value,
        inputClasses,
        name: phoneState.name,
        onChange: onChangeValueOnly,
        onBlur: handleOnBlur,
        "aria-invalid": !isValid,
        "aria-required": required,
        ...rest,
        "aria-describedby": ariaDescribedBy,
        PhoneNumberInputComponent: PhoneNumberInputModule.default
      }
    }
  );
};
const ConnectedPhoneNumberInput = ({
  onBlur,
  onChange,
  ...rest
}) => {
  return /* @__PURE__ */ React.createElement(FormContext.Consumer, null, (context = {}) => /* @__PURE__ */ React.createElement(
    PhoneNumberInputContainer,
    {
      ...rest,
      onBlur: context.onBlur || onBlur,
      onChange: context.onChange || onChange,
      initialState: context.getInitialState && context.getInitialState(rest.id) || rest.initialState && rest.initialState
    }
  ));
};
const LazyPhoneNumberInput = (props) => {
  const [module, loading, error] = usePhoneNumberInputPackage();
  const { getLabel } = useIntl();
  const FormControlWrapperProps = {
    className: props.className,
    label: props.label,
    hideAsteriskSign: props.hideAsteriskSign,
    tooltip: props.tooltip,
    optionalText: props.optionalText,
    errorMessageId: `${props.id}-error-message`,
    errorMessage: props.errorMessage,
    displayError: false
  };
  if (loading) {
    return /* @__PURE__ */ React.createElement(FormControlFieldWrapper, { ...FormControlWrapperProps, required: props.required }, /* @__PURE__ */ React.createElement("div", { className: "phone-number-input__skeleton-loader" }, /* @__PURE__ */ React.createElement(
      Skeleton,
      {
        height: 50,
        width: "100%",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        "aria-label": getLabel("common.loadingText")
      },
      /* @__PURE__ */ React.createElement("rect", { width: "100%", height: "50px", rx: "5", fill: "currentColor" })
    )));
  }
  if (!module || error) {
    return /* @__PURE__ */ React.createElement(FormControlFieldWrapper, { ...FormControlWrapperProps, required: props.required }, /* @__PURE__ */ React.createElement(Alert, { variant: "error", className: "alert-m-default" }, getLabel("common.error.network")));
  }
  return /* @__PURE__ */ React.createElement(ConnectedPhoneNumberInput, { ...props, PhoneNumberInputModule: module });
};
export {
  ConnectedPhoneNumberInput,
  PhoneNumberInputContainer,
  LazyPhoneNumberInput as default
};
//# sourceMappingURL=PhoneNumberInputContainer.js.map
